import {
  getContentType,
  getContentTypeCategory,
} from '@carboncopyco/editor-utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useModifiersBuilder } from '../../../CreateTransactionRuleModal';
import {
  RuleDescriptionField,
  RuleNameTitleField,
} from '../../../TransactionRuleForm';
import {
  CancelButton,
  FormLayout,
  Separator,
} from '../../../TransactionRuleModal';
import { useEditTransactionRuleContext } from '../../EditTransactionRuleContext';
import { useFormDirtyFields } from '../../useFormDirtyFields';
import { ChangesCounter } from './ChangesCounter';
import { SaveButton } from './SaveButton';
import { StepButton } from './StepButton';

export const AdminForm = () => {
  const { editorDataRulesResponseVisualization } = useFlags();
  const {
    submitEdit,
    cancelEdit,
    transactionRule,
    goToSelectorsStep,
    goToModifiersStep,
    goToVisualizationStep,
    form,
  } = useEditTransactionRuleContext();
  const [isSubmitting, setIsSubmitting] = useBoolean();
  const { handleSubmit, formState, getValues, watch } = form;
  const { isFormDirty } = useFormDirtyFields();

  const responseType = watch('responseType');
  const headers = watch('headers');
  const contentType = getContentType(headers ?? []);
  const contentTypeCategory = getContentTypeCategory(contentType);
  const allowVisualizationStep =
    editorDataRulesResponseVisualization &&
    responseType === 'static' &&
    contentTypeCategory === 'json';

  const { buildModifiers, isModifiersFormFieldsDirty } = useModifiersBuilder({
    getFormValues: () => form.getValues(),
  });

  const submitForm = async (refresh = true) => {
    const { name, description, selectors } = getValues();
    setIsSubmitting.setTrue();
    await submitEdit(
      {
        ...transactionRule,
        name: name.trim(),
        description,
        rules: [
          {
            ...transactionRule.rules[0],
            selectors: JSON.parse(selectors),
            modifiers: isModifiersFormFieldsDirty(formState.dirtyFields)
              ? buildModifiers()
              : transactionRule.rules[0].modifiers,
          },
        ],
      },
      refresh
    );
    setIsSubmitting.setFalse();
  };

  return (
    <FormLayout onSubmit={handleSubmit(submitForm)}>
      <div className="flex-1">
        <div className="pt-5 px-5 pb-5 flex flex-col gap-2">
          <RuleNameTitleField />
          <RuleDescriptionField
            hideLabel
            rows={3}
          />
        </div>
        <Separator />
        <StepButton
          title="Selectors"
          iconType="selectors"
          onClick={goToSelectorsStep}
        />
        <Separator />
        <StepButton
          title="Data"
          iconType="modifiers"
          onClick={goToModifiersStep}
        />
        {allowVisualizationStep && (
          <>
            <Separator />
            <StepButton
              title="Rule visualization"
              iconType="visualization"
              onClick={goToVisualizationStep}
            />
          </>
        )}
      </div>
      <FormLayout.Footer>
        <ChangesCounter />
        <div className="flex-1" />
        <CancelButton
          onClick={cancelEdit}
          disabled={isSubmitting}
        />
        <SaveButton
          onClick={(shouldRefresh) =>
            handleSubmit(() => submitForm(shouldRefresh))()
          }
          disabled={!isFormDirty() || !formState.isValid || isSubmitting}
        />
      </FormLayout.Footer>
    </FormLayout>
  );
};
